import {Injectable, inject} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {environment} from '../environments/environment';
import {AdminChatAnalyticsRecalculationStatus, AdminDiscordBotSettings, AdminFeatureFlag, AdminInfrastructureHistory, AdminLevelRecalculationStatus, AdminManagedScheduler, AdminManagedSchedulerRunResult, AdminManagedSchedulerUpdateRequest, AdminOverview, AdminSiteAnalyticsOverview, AdminTwitchBotRegistryOverview, AdminTwitchChatRoutingEntry, AdminTwitchChatbotDiagnostics, AdminTwitchFollowerSyncStatus, AdminTwitchModerationAnalyticsOverview, AdminUser, AdminUserDetails, AuditEvent, AuditPage, DiscordChannelInfo, DiscordGuild, DiscordLinkedServer, SupportSession, SupportSessionResponse} from './admin.models';

@Injectable({ providedIn: 'root' })
export class AdminApiService {
  private static readonly monitoringAuthStorageKey = 'freyr-admin-monitoring-auth';

  private readonly http = inject(HttpClient);
  private readonly securityBase = environment.securityApiUrl || environment.apiUrl.replace(/\/api\/?$/, '');
  private readonly metricsBase = environment.metricsApiUrl || 'http://localhost:8094';
  private monitoringAuthorization = sessionStorage.getItem(AdminApiService.monitoringAuthStorageKey);

  readonly logoutUrl = `${this.securityBase}/auth/logout`;
  readonly overviewStreamUrl = `${this.securityBase}/admin/overview/stream`;
  readonly levelRecalculationWebSocketUrl = this.toWebSocketUrl(`${this.securityBase}/admin/levels/recalculation/ws`);
  readonly twitchFollowerSyncWebSocketUrl = this.toWebSocketUrl(`${this.securityBase}/admin/twitch/followers/sync/ws`);

  get loginUrl(): string {
    const returnTo = typeof window !== 'undefined'
      ? window.location.href
      : environment.webAppUrl;
    const state = `admin:${encodeURIComponent(returnTo)}`;
    return `${this.securityBase}/auth/twitch/login/redirect?state=${encodeURIComponent(state)}`;
  }

  refreshSession(): Observable<unknown> {
    return this.http.post(`${this.securityBase}/auth/refresh`, null, { withCredentials: true });
  }

  getCurrentUser(): Observable<AdminUser> {
    return this.http.get<AdminUser>(`${this.securityBase}/me`, { withCredentials: true });
  }

  getOverview(includeInfrastructure = false): Observable<AdminOverview> {
    return this.http.get<AdminOverview>(`${this.securityBase}/admin/overview`, {
      withCredentials: true,
      params: new HttpParams().set('includeInfrastructure', String(includeInfrastructure))
    });
  }

  getSiteAnalytics(days = 14): Observable<AdminSiteAnalyticsOverview> {
    return this.http.get<AdminSiteAnalyticsOverview>(`${this.securityBase}/admin/site-analytics`, {
      withCredentials: true,
      params: new HttpParams().set('days', String(Math.min(Math.max(days, 1), 90)))
    });
  }

  getChatAnalyticsRecalculationStatus(): Observable<AdminChatAnalyticsRecalculationStatus> {
    return this.http.get<AdminChatAnalyticsRecalculationStatus>(`${this.securityBase}/admin/chat-analytics/recalculation`, {
      withCredentials: true
    });
  }

  startChatAnalyticsRecalculation(): Observable<AdminChatAnalyticsRecalculationStatus> {
    return this.http.post<AdminChatAnalyticsRecalculationStatus>(`${this.securityBase}/admin/chat-analytics/recalculation`, {}, {
      withCredentials: true
    });
  }

  getLevelRecalculationStatus(): Observable<AdminLevelRecalculationStatus> {
    return this.http.get<AdminLevelRecalculationStatus>(`${this.securityBase}/admin/levels/recalculation`, {
      withCredentials: true
    });
  }

  startLevelRecalculation(userId?: string | null): Observable<AdminLevelRecalculationStatus> {
    let params = new HttpParams();
    const normalizedUserId = userId?.trim();
    if (normalizedUserId) {
      params = params.set('userId', normalizedUserId);
    }
    return this.http.post<AdminLevelRecalculationStatus>(`${this.securityBase}/admin/levels/recalculation`, {}, {
      withCredentials: true,
      params
    });
  }

  getTwitchFollowerSyncStatus(): Observable<AdminTwitchFollowerSyncStatus> {
    return this.http.get<AdminTwitchFollowerSyncStatus>(`${this.securityBase}/admin/twitch/followers/sync`, {
      withCredentials: true
    });
  }

  startTwitchFollowerSync(): Observable<AdminTwitchFollowerSyncStatus> {
    return this.http.post<AdminTwitchFollowerSyncStatus>(`${this.securityBase}/admin/twitch/followers/sync`, {}, {
      withCredentials: true
    });
  }

  getTwitchModerationAnalytics(days = 14, granularitySeconds?: number | null, from?: string | null, to?: string | null): Observable<AdminTwitchModerationAnalyticsOverview> {
    let params = new HttpParams().set('days', String(Math.min(Math.max(days, 1), 90)));
    if (granularitySeconds) {
      params = params.set('granularitySeconds', String(Math.max(1, Math.floor(granularitySeconds))));
    }
    if (from) {
      params = params.set('from', from);
    }
    if (to) {
      params = params.set('to', to);
    }
    return this.http.get<AdminTwitchModerationAnalyticsOverview>(`${this.securityBase}/admin/twitch/moderation-analytics`, {
      withCredentials: true,
      params
    });
  }

  getOverviewHistory(request: { minutes?: number; from?: string | null; to?: string | null; granularitySeconds?: number | null } = { minutes: 60 }): Observable<AdminInfrastructureHistory> {
    let params = new HttpParams();
    if (request.from || request.to) {
      if (request.from) {
        params = params.set('from', request.from);
      }
      if (request.to) {
        params = params.set('to', request.to);
      }
    } else {
      params = params.set('minutes', String(request.minutes ?? 60));
    }
    if (request.granularitySeconds) {
      params = params.set('granularitySeconds', String(request.granularitySeconds));
    }
    return this.http.get<AdminInfrastructureHistory>(`${this.securityBase}/admin/overview/history`, {
      withCredentials: true,
      params
    });
  }

  isMonitoringMode(): boolean {
    return !!this.monitoringAuthorization;
  }

  setMonitoringCredentials(username: string, password: string): Observable<{ username: string; mode: string }> {
    this.monitoringAuthorization = `Basic ${btoa(`${username}:${password}`)}`;
    sessionStorage.setItem(AdminApiService.monitoringAuthStorageKey, this.monitoringAuthorization);
    return this.checkMonitoringAccess();
  }

  clearMonitoringCredentials(): void {
    this.monitoringAuthorization = null;
    sessionStorage.removeItem(AdminApiService.monitoringAuthStorageKey);
  }

  checkMonitoringAccess(): Observable<{ username: string; mode: string }> {
    return this.http.get<{ username: string; mode: string }>(`${this.metricsBase}/monitoring/whoami`, {
      headers: this.monitoringHeaders()
    });
  }

  getMonitoringOverview(): Observable<AdminOverview> {
    return this.http.get<AdminOverview['infrastructure']>(`${this.metricsBase}/monitoring/overview`, {
      headers: this.monitoringHeaders()
    }).pipe(
      map((infrastructure) => ({
        totalUsers: 0,
        adminUsers: 0,
        supportUsers: 0,
        linkedTwitchAccounts: 0,
        linkedDiscordAccounts: 0,
        linkedSpotifyAccounts: 0,
        activeSupportSessions: 0,
        failedAuditEvents: 0,
        recentFailedAuditEvents: [] as AuditEvent[],
        infrastructure
      }))
    );
  }

  getMonitoringOverviewHistory(request: { minutes?: number; from?: string | null; to?: string | null; granularitySeconds?: number | null } = { minutes: 60 }): Observable<AdminInfrastructureHistory> {
    let params = new HttpParams();
    if (request.from || request.to) {
      if (request.from) {
        params = params.set('from', request.from);
      }
      if (request.to) {
        params = params.set('to', request.to);
      }
    } else {
      params = params.set('minutes', String(request.minutes ?? 60));
    }
    if (request.granularitySeconds) {
      params = params.set('granularitySeconds', String(request.granularitySeconds));
    }
    return this.http.get<AdminInfrastructureHistory>(`${this.metricsBase}/monitoring/history`, {
      headers: this.monitoringHeaders(),
      params
    });
  }

  listFeatureFlags(): Observable<AdminFeatureFlag[]> {
    return this.http.get<AdminFeatureFlag[]>(`${this.securityBase}/admin/feature-flags`, { withCredentials: true });
  }

  updateFeatureFlag(featureKey: string, status: string, reason?: string | null): Observable<AdminFeatureFlag> {
    return this.http.patch<AdminFeatureFlag>(`${this.securityBase}/admin/feature-flags/${encodeURIComponent(featureKey)}`, {
      status,
      reason: reason?.trim() || null
    }, { withCredentials: true });
  }

  getDiscordBotSettings(): Observable<AdminDiscordBotSettings> {
    return this.http.get<AdminDiscordBotSettings>(`${this.securityBase}/admin/discord-bot`, { withCredentials: true });
  }

  updateDiscordBotSettings(request: {
    username?: string | null;
    avatarUrl?: string | null;
    applicationDescription?: string | null;
    activityType?: string | null;
    activityText?: string | null;
    releaseAnnouncementsChannelId?: string | null;
    releaseAnnouncementsGuildId?: string | null;
    reason?: string | null;
  }): Observable<AdminDiscordBotSettings> {
    return this.http.patch<AdminDiscordBotSettings>(`${this.securityBase}/admin/discord-bot`, request, { withCredentials: true });
  }

  publishDiscordReleaseAnnouncement(request: {
    channelId?: string | null;
    name?: string | null;
    tagName?: string | null;
    description?: string | null;
    url?: string | null;
    publishedAt?: string | null;
    commitSha?: string | null;
    reason?: string | null;
  }): Observable<{ published: boolean; status: string }> {
    return this.http.post<{ published: boolean; status: string }>(
      `${this.securityBase}/admin/discord-bot/release-announcements/publish`,
      request,
      { withCredentials: true }
    );
  }

  getDiscordLinkUrl(state = 'admin:discord-bot'): Observable<string> {
    return this.http.get<{ url: string }>(`${this.securityBase}/auth/discord/login`, {
      withCredentials: true,
      params: new HttpParams().set('state', state)
    }).pipe(map(response => response.url));
  }

  listDiscordLinkedServers(): Observable<DiscordLinkedServer[]> {
    return this.http.get<DiscordLinkedServer[]>(`${this.securityBase}/me/discord/linked-servers`, { withCredentials: true });
  }

  listDiscordGuilds(): Observable<DiscordGuild[]> {
    return this.http.get<DiscordGuild[]>(`${this.securityBase}/me/discord/guilds`, { withCredentials: true });
  }

  linkDiscordServer(guildId: string, guildName?: string | null): Observable<DiscordLinkedServer> {
    return this.http.post<DiscordLinkedServer>(`${this.securityBase}/me/discord/linked-servers`, {
      guildId,
      guildName: guildName?.trim() || null
    }, { withCredentials: true });
  }

  getDiscordGuildChannels(guildId: string): Observable<DiscordChannelInfo[]> {
    return this.http.get<DiscordChannelInfo[]>(`${this.securityBase}/me/schedule-discord/guilds/${encodeURIComponent(guildId)}/channels`, {
      withCredentials: true
    });
  }

  listSchedulers(): Observable<AdminManagedScheduler[]> {
    return this.http.get<AdminManagedScheduler[]>(`${this.securityBase}/admin/schedulers`, { withCredentials: true });
  }

  updateScheduler(schedulerKey: string, request: AdminManagedSchedulerUpdateRequest): Observable<AdminManagedScheduler> {
    return this.http.patch<AdminManagedScheduler>(
      `${this.securityBase}/admin/schedulers/${encodeURIComponent(schedulerKey)}`,
      request,
      { withCredentials: true }
    );
  }

  runScheduler(schedulerKey: string): Observable<AdminManagedSchedulerRunResult> {
    return this.http.post<AdminManagedSchedulerRunResult>(
      `${this.securityBase}/admin/schedulers/${encodeURIComponent(schedulerKey)}/run`,
      {},
      { withCredentials: true }
    );
  }

  getTwitchBotRegistryOverview(): Observable<AdminTwitchBotRegistryOverview> {
    return this.http.get<AdminTwitchBotRegistryOverview>(`${this.securityBase}/admin/twitch-bots`, { withCredentials: true });
  }

  refreshTwitchBotRegistry(): Observable<AdminTwitchBotRegistryOverview> {
    return this.http.post<AdminTwitchBotRegistryOverview>(
      `${this.securityBase}/admin/twitch-bots/refresh`,
      {},
      { withCredentials: true }
    );
  }

  listTwitchChatRouting(): Observable<AdminTwitchChatRoutingEntry[]> {
    return this.http.get<AdminTwitchChatRoutingEntry[]>(`${this.securityBase}/admin/twitch-bots/chat-routing`, { withCredentials: true });
  }

  listTwitchChatbotDiagnostics(): Observable<AdminTwitchChatbotDiagnostics[]> {
    return this.http.get<AdminTwitchChatbotDiagnostics[]>(`${this.securityBase}/admin/twitch-bots/chat-routing/diagnostics`, { withCredentials: true });
  }

  updateTwitchChatbotStrictMode(broadcasterId: string, chatbotStrictHelixOnly: boolean): Observable<AdminTwitchChatRoutingEntry> {
    return this.http.patch<AdminTwitchChatRoutingEntry>(
      `${this.securityBase}/admin/twitch-bots/chat-routing/${encodeURIComponent(broadcasterId)}/strict`,
      { chatbotStrictHelixOnly },
      { withCredentials: true }
    );
  }

  listUsers(): Observable<AdminUser[]> {
    return this.http.get<AdminUser[]>(`${this.securityBase}/admin/users`, { withCredentials: true });
  }

  getUserDetails(userId: string): Observable<AdminUserDetails> {
    return this.http.get<AdminUserDetails>(`${this.securityBase}/admin/users/${userId}`, { withCredentials: true });
  }

  updateUserRoles(userId: string, roles: string[], reason?: string | null): Observable<AdminUser> {
    return this.http.patch<AdminUser>(`${this.securityBase}/admin/users/${userId}/roles`, {
      roles,
      reason: reason?.trim() || null
    }, { withCredentials: true });
  }

  listAuditEvents(filters: {
    query?: string;
    actorUserId?: string;
    effectiveUserId?: string;
    supportSessionId?: string;
    actionType?: string;
    requestId?: string;
    success?: string;
  }): Observable<AuditPage> {
    let params = new HttpParams().set('page', '0').set('size', '20');

    if (filters.query?.trim()) {
      params = params.set('query', filters.query.trim());
    }
    if (filters.actorUserId?.trim()) {
      params = params.set('actorUserId', filters.actorUserId.trim());
    }
    if (filters.effectiveUserId?.trim()) {
      params = params.set('effectiveUserId', filters.effectiveUserId.trim());
    }
    if (filters.supportSessionId?.trim()) {
      params = params.set('supportSessionId', filters.supportSessionId.trim());
    }
    if (filters.actionType?.trim()) {
      params = params.set('actionType', filters.actionType.trim());
    }
    if (filters.requestId?.trim()) {
      params = params.set('requestId', filters.requestId.trim());
    }
    if (filters.success === 'true' || filters.success === 'false') {
      params = params.set('success', filters.success);
    }

    return this.http.get<AuditPage>(`${this.securityBase}/admin/audit-events`, {
      withCredentials: true,
      params
    });
  }

  getAuditEvent(auditEventId: string): Observable<AuditEvent> {
    return this.http.get<AuditEvent>(`${this.securityBase}/admin/audit-events/${auditEventId}`, {
      withCredentials: true
    });
  }

  createSupportSession(request: {
    targetUserId: string;
    permissions: string[];
    reason: string;
  }): Observable<SupportSessionResponse> {
    return this.http.post<SupportSessionResponse>(`${this.securityBase}/admin/support-sessions`, request, {
      withCredentials: true
    });
  }

  listSupportSessions(status?: string): Observable<SupportSession[]> {
    let params = new HttpParams();
    if (status?.trim()) {
      params = params.set('status', status.trim());
    }
    return this.http.get<SupportSession[]>(`${this.securityBase}/admin/support-sessions`, {
      withCredentials: true,
      params
    });
  }

  resumeSupportSession(supportSessionId: string): Observable<SupportSessionResponse> {
    return this.http.post<SupportSessionResponse>(`${this.securityBase}/admin/support-sessions/${supportSessionId}/resume`, {}, {
      withCredentials: true
    });
  }

  closeSupportSession(supportSessionId: string, reason?: string | null): Observable<void> {
    return this.http.post<void>(`${this.securityBase}/admin/support-sessions/${supportSessionId}/close`, {
      reason: reason?.trim() || null
    }, {
      withCredentials: true
    });
  }

  private monitoringHeaders(): HttpHeaders {
    let headers = new HttpHeaders();
    if (this.monitoringAuthorization) {
      headers = headers.set('Authorization', this.monitoringAuthorization);
    }
    return headers;
  }

  private toWebSocketUrl(url: string): string {
    if (url.startsWith('https://')) {
      return `wss://${url.slice('https://'.length)}`;
    }
    if (url.startsWith('http://')) {
      return `ws://${url.slice('http://'.length)}`;
    }
    return url;
  }
}
